import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom/client";

import {
  CookieService,
  CookieBanner as CookieBannerComponent,
  FontFamily,
} from "@biskoui/cookie-banner";
// Import CSS as strings
import bannerStyles from "@biskoui/cookie-banner/dist/CookieBanner.scss";
import localStyles from "./style.scss"; // Make sure this path is correct

import {
  initializeServiceGlobally,
  initializeServiceDynamically,
  acceptAll,
  rejectAll,
  acceptCustomized,
  updateConsent,
  activateService,
  readAcceptedServices,
} from "./services";
import { CDN_URL, API_URL } from "./config";
import { CookieBanner } from "./types";
import { IS_BOT } from "./botDetection";
import { BISKOUI_SETTINGS, SESSION_ID } from "./utils";
import { initialize as analyticsInitialize } from "./analytics";

// Function to inject styles, waiting for DOM if needed
function injectStyles(cssString: string) {
  if (typeof document === 'undefined') return; // Guard for non-browser environments

  const createStyleElement = () => {
    if (!document.head) return; // Head might still not be ready
    const style = document.createElement('style');
    style.textContent = cssString;
    document.head.appendChild(style);
  };

  if (document.head) {
    createStyleElement();
  } else {
    // If head is not ready, wait for DOMContentLoaded
    document.addEventListener('DOMContentLoaded', createStyleElement, { once: true });
  }
}

// Inject the combined styles
injectStyles(bannerStyles + localStyles);

(function () {
  //@ts-ignore
  if (window.biskouiScriptLoaded) {
    return;
  }
  //@ts-ignore
  window.biskouiScriptLoaded = true;

  //@ts-ignore
  window.dataLayer = window.dataLayer || [];
  //@ts-ignore
  window.uetq = window.uetq || [];

  analyticsInitialize()

  //@ts-ignore
  function dataLayerPush() {
    //@ts-ignore
    window.dataLayer.push(arguments);
  }

  // Initialize the biskouiShowBanner function here, re-define it below to actually show the banner.
  //@ts-ignore
  window.biskouiShowBanner = () => {};

  function App({
    cookieBanner,
    acceptedServicesDefault,
    hideBannerDefault,
  }: {
    cookieBanner: CookieBanner;
    acceptedServicesDefault: CookieService[];
    hideBannerDefault: boolean;
  }) {
    const [acceptedServices, setAcceptedServices] = useState<CookieService[]>(
      acceptedServicesDefault
    );

    // The option to reshow the banner can be triggered by a global function, and is only available
    // while the banner is hidden. We use a key id to make sure we reset the internal state of the banner
    // as well (to override its own visible state).
    const [reshowBanner, setReshowBanner] = useState<boolean>(
      !hideBannerDefault
    );
    const [bannerKeyId, setBannerKeyId] = useState<number>(0);

    useEffect(() => {
      //@ts-ignore
      window.biskouiShowBanner = () => {
        if (!reshowBanner) setReshowBanner(true);
      };
    });

    // When we want to reshow the banner, we increment the banner key id to reset it.
    useEffect(() => {
      if (reshowBanner) {
        setBannerKeyId(bannerKeyId + 1);
      }
    }, [reshowBanner]);

    const onAcceptAll = () => {
      acceptAll(cookieBanner);
      setAcceptedServices(cookieBanner.cookieServices);
    };
    const onRejectAll = () => {
      rejectAll(cookieBanner);
      setAcceptedServices([]);
    };
    const onAcceptCustomizedCookies = (services: CookieService[]) => {
      acceptCustomized(cookieBanner, services);
      setAcceptedServices(services);
    };

    let cookiePolicy = cookieBanner.cookiePolicy;
    cookieBanner.localizedCookiePolicies.forEach((lcp) => {
      if (lcp.language == cookieBanner.language)
        cookiePolicy = lcp.cookiePolicy;
    });

    if (BISKOUI_SETTINGS.hidden) return <div></div>;

    return (
      <CookieBannerComponent
        siteName="regblanc.com"
        key={bannerKeyId}
        hideBannerDefault={hideBannerDefault && !reshowBanner}
        layout={cookieBanner.layout}
        primaryColor={cookieBanner.primaryColor}
        backgroundColor={cookieBanner.backgroundColor}
        fontColor={cookieBanner.fontColor}
        buttonFontColor={cookieBanner.buttonFontColor}
        inheritFontFamily={cookieBanner.inheritFontFamily}
        fontFamily={cookieBanner.fontFamily as FontFamily}
        borderStyle={cookieBanner.borderStyle}
        bannerBorderColor={cookieBanner.bannerBorderColor}
        bannerBorderWidth={cookieBanner.bannerBorderWidth}
        buttonBorderColor={cookieBanner.bannerButtonBorderColor}
        buttonBorderWidth={cookieBanner.bannerButtonBorderWidth}
        headingColor={cookieBanner.bannerHeadingColor}
        buttonColor={cookieBanner.bannerButtonColor}
        toggleColor={cookieBanner.bannerToggleColor}
        bannerPosition={cookieBanner.bannerPosition}
        stickerPosition={cookieBanner.stickerPosition}
        languageCode={cookieBanner.language}
        cookiePolicy={cookiePolicy}
        cookieWallMode={
          cookieBanner.cookieWallMode ??
          ((cookieBanner.cookieWallEnabled && !hideBannerDefault && !IS_BOT) ? "on" : "off")
        }
        cookieWallExceptions={cookieBanner.cookieWallExceptions}
        cookieServices={cookieBanner.cookieServices}
        compact={cookieBanner.bannerCompact}
        discreteBranding={cookieBanner.bannerDiscreteBranding ?? undefined}
        disableFocusMainButton={cookieBanner.bannerDisableFocusMainButton ?? undefined}
        acceptedServicesDefault={acceptedServices}
        onAcceptAll={onAcceptAll}
        onRejectAll={onRejectAll}
        onAcceptCustomizedCookies={onAcceptCustomizedCookies}
        onClose={() => setReshowBanner(false)}
        onOpen={() => setReshowBanner(true)}
      />
    );
  }

  function renderBanner(
    cookieBanner: CookieBanner,
    hideBannerDefault: boolean,
    acceptedServicesDefault: CookieService[]
  ) {
    const bannerReactRoot = ReactDOM.createRoot(
      document.getElementById("biskoui-mount") as HTMLElement
    );
    bannerReactRoot.render(
      <React.StrictMode>
        <App
          cookieBanner={cookieBanner}
          hideBannerDefault={hideBannerDefault}
          acceptedServicesDefault={acceptedServicesDefault}
        />
      </React.StrictMode>
    );
  }

  // We preload the font that will be used in the banner as soon as we get them, so that we don't have to wait
  // for the font to load when we toggle the banner. We don't load extra fonts for crawler though, to
  // avoid slower loading time and load on the server.
  var fontLoadingPromise: undefined | Promise<any> = undefined;

  // We start loading the banner details for the client before waiting for the page to load.
  var loadBannerPromise: Promise<CookieBanner | void> = fetch(
    `${API_URL}/v1/cookie-banners/${BISKOUI_SETTINGS.clientId}`,
    {
      method: "GET",
    }
  )
    .then((res) => {
      if (res.status === 200) {
      } else if (res.status === 401) {
        console.log("Not authenticated as a valid user yet");
      }
      return res.json();
    })
    .then((cookieBanner: CookieBanner) => {
      // TODO: this should probably come from the backend directly?
      cookieBanner.id = BISKOUI_SETTINGS.clientId;

      // If it's a bot, we don't want to do any of the following (record a view and loading fonts).
      if (IS_BOT) return cookieBanner;

      // If the banner was set by wordpress but the domain does not support the plugin, we do nothing
      // as we will reject the banner in the code below.
      if (
        BISKOUI_SETTINGS.configuredByWordPress &&
        !cookieBanner.wordPressPluginEnabled
      )
        return cookieBanner;

      if ("fonts" in document && cookieBanner.fontFamily) {
        fontLoadingPromise = document.fonts.load(
          "1em " + cookieBanner.fontFamily
        ).catch(e => {
          console.warn("Failed to load custom font, continuing with potentially invalid fonts", e);
          return Promise.resolve(); // Resolve the promise so the banner still gets inserted
        });
      }

      return {
        ...cookieBanner,
        cookieServices: cookieBanner.cookieServices.map((cs) => ({
          ...cs,
          iconPath: CDN_URL + "/cookie-services-icons/" + cs.iconPath,
        })),
      };
    })
    .catch((e: Error) => {
      // This can happen either if something unexpected happened with the
      // network, or otherwise if the Browser had security policies in place to
      // prevent CORS (eventhough this should be valid).
      //
      // Either way, we do nothing and silently fail.
      console.log(
        "Unexpected error when requesting the cookie banner, cannot load the banner"
      );
    });

  // Code to run when the DOM is ready.
  function initialize() {
    // We append new code to run after the fetch promise.
    loadBannerPromise.then((cookieBanner: CookieBanner | void) => {
      // It can be void if it threw an exception above.
      if (!cookieBanner) {
        return;
      }
      var consentPopupRoot = document.createElement("div");
      consentPopupRoot.setAttribute("id", "biskoui-mount");
      consentPopupRoot.setAttribute("class", "biskoui-mount");
      document.body.appendChild(consentPopupRoot);

      // If the banner is configured by WordPress but the domain does not support it, we do not insert it.
      // TODO: maybe we want to display some banner with error (like when out of views quota) instead of just doing nothing.
      if (
        BISKOUI_SETTINGS.configuredByWordPress &&
        !cookieBanner.wordPressPluginEnabled
      ) {
        console.error("Domain tier does not support word press plugin");
        return;
      }

      // First, we initialize Google Consent mode, if the settings is set.
      // Note that this happens only after fetching the banner info, so it's important to not initialize any other services
      // before (starting global tags), instead these tags should be started by the biskoui banner.
      if (cookieBanner.googleConsentMode !== "disabled") {
        //@ts-ignore
        dataLayerPush("consent", "default", {
          ad_storage: "denied",
          ad_user_data: "denied",
          ad_personalization: "denied",
          analytics_storage: cookieBanner.analyticsStorageGrantedByDefault
            ? "granted"
            : "denied",
          // These two are apparently mandatory to be used in Europe, and it is not practical to ask for their consent.
          functionality_storage: "granted",
          security_storage: "granted",
          // This is apparently used for personalized ads, so we activate it at the same time as the ad_* consents.
          personalization_storage: "denied",

          wait_for_update: "1000",
        });
      }

      if (cookieBanner.uetConsentMode !== "disabled") {
        //@ts-ignore
        window.uetq.push("consent", "default", {
          ad_storage: "denied",
        });
      }

      // TODO: we need to handle versionning of the banner, when new services are added/removed.
      // We check local storage to see if we should auto-apply accept/reject.
      var hideBannerDefault = false; // By default we show the banner
      var acceptedServicesDefault: CookieService[] = [];
      if (!IS_BOT) {
        // We do not want to trigger any active service or sticker banner for bots.
        let acceptedServices = readAcceptedServices(cookieBanner);
        if (acceptedServices === undefined) {
          acceptedServicesDefault = [];
          hideBannerDefault = false;
        } else {
          acceptedServicesDefault = acceptedServices;
          hideBannerDefault = true;
        }
      }

      // Now we perform the built-in onload events.
      for (var i = 0; i < cookieBanner.cookieServices.length; i++) {
        let cookieService = cookieBanner.cookieServices[i];
        initializeServiceGlobally(cookieService, cookieBanner, () => {
          // We create a new banner, essentially forcing a reload of the react banner, and simply
          // adding the service to the list of default accepted services (as we know this is always an accept action).
          // TODO: This is not ideal, it would be better to be able to hook into the banner with all
          // the events, but this is more work as we would need to refactor the banner.
          // This also doesn't work in all cases as it reloads the banner, it will bring it back to the initial state of the page.
          renderBanner(cookieBanner, hideBannerDefault, [
            ...acceptedServicesDefault,
            cookieService,
          ]);
          acceptCustomized(cookieBanner, [
            ...acceptedServicesDefault,
            cookieService,
          ]);
        });
      }

      // We configure an observer for the entire document to continuously initialize services on dynamically added elements.
      var observerConfig = {
        attributes: true,
        childList: true,
        characterData: true,
      };
      var domObserver = new MutationObserver(function (
        mutationsList,
        observer
      ) {
        for (let mutation of mutationsList) {
          if (mutation.type === "childList") {
            // Check for added nodes
            mutation.addedNodes.forEach((node) => {
              if (!(node instanceof HTMLElement)) {
                return;
              }
              observer.disconnect();

              for (var i = 0; i < cookieBanner.cookieServices.length; i++) {
                let cookieService = cookieBanner.cookieServices[i];
                initializeServiceDynamically(
                  cookieService,
                  cookieBanner,
                  node,
                  () => {
                    // TODO: this is copy-pasted from the initializeServiceGlobally above, not very clean and not very DRY.
                    renderBanner(cookieBanner, hideBannerDefault, [
                      ...acceptedServicesDefault,
                      cookieService,
                    ]);
                    acceptCustomized(cookieBanner, [
                      ...acceptedServicesDefault,
                      cookieService,
                    ]);
                  }
                );
              }

              observer.observe(document.body, observerConfig);
            });
          }
        }
      });
      domObserver.observe(document.body, observerConfig);

      // Finally we trigger the services that should immediately be triggered.
      for (var i = 0; i < cookieBanner.cookieServices.length; i++) {
        let cookieService = cookieBanner.cookieServices[i];
        var isAccepted = acceptedServicesDefault.some(
          (s) => s.id == cookieService.id
        );
        if (isAccepted) {
          updateConsent(cookieService, cookieBanner, "granted");
          activateService(cookieService);
        } else {
          // If the service is not accepted, and the visitor had made a persisted choice, it means it was previously rejected.
          if (hideBannerDefault) {
            updateConsent(cookieService, cookieBanner, "denied");
          }

          // If we are in google consent mode advanced and this is one of the Google service,
          // we launch them immediately even without an accept action.
          if (
            cookieBanner.googleConsentMode === "advanced" &&
            (cookieService.stableName == "google_analytics" ||
              cookieService.stableName == "google_ads" ||
              cookieService.stableName == "google_ads_remarketing" ||
              cookieService.stableName == "google_conversion_linker")
          ) {
            activateService(cookieService);
          }

          // If we are in uet consent mode advanced and this is one of the Microsoft service,
          // we launch them immediately even without an accept action.
          if (
            cookieBanner.uetConsentMode === "advanced" &&
            cookieService.stableName == "ms_advertising"
          ) {
            activateService(cookieService);
          }
        }
      }

      const insertBanner = () => {
        // We notify GTM that biskoui is ready, this is a hook that can be used to start scripts that depends on biskoui
        // being ready (for example, initializing consent-aware scripts, because this events guarantees that we have initialized the default consent mode).
        //@ts-ignore
        window.dataLayer.push({ event: "biskoui_ready" });
        renderBanner(cookieBanner, hideBannerDefault, acceptedServicesDefault);
      };

      let viewLogged = false;
      let logView = function () {
        if (viewLogged || IS_BOT) return;
        viewLogged = true;

        fetch(
          `${API_URL}/v1/cookie-banners/${BISKOUI_SETTINGS.clientId}/view`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              sessionId: SESSION_ID,
              bannerHidden: hideBannerDefault,
            }),
          }
        );
      };

      // Only log the view once the tab becomes visible.
      if (document.visibilityState == "visible") {
        // TODO: remove delay for logging view.
        setTimeout(logView, 500);
      } else {
        document.addEventListener("visibilitychange", function logData() {
          if (document.visibilityState === "visible") {
            // TODO: remove delay for logging view.
            setTimeout(logView, 500);
          }
        });
      }

      // The amount of milliseonds we want to wait before showing the banner, once the DOM is loaded.
      // TODO: this should be chosen by the user, depending on the effect they want to see.
      const popDelay: number = 0;

      // We show the banner immediately if it shows as a sticker or if it is a crawler (to make sure they record the backlink).
      if (IS_BOT || hideBannerDefault) {
        if (fontLoadingPromise)
          fontLoadingPromise.then(() => {
            insertBanner();
          });
        else insertBanner();
      } else {
        if (popDelay <= 0) {
          if (fontLoadingPromise) {
            fontLoadingPromise.then(() => {
              insertBanner();
            });
          } else {
            insertBanner();
          }
        } else {
          // Otherwise, we wait X seconds in order to have the banner "pop"
          window.setTimeout(() => {
            // We also check that the font was loaded, if not we wait for this to complete.
            if (fontLoadingPromise)
              fontLoadingPromise.then(() => {
                insertBanner();
              });
            else insertBanner();
          }, popDelay);
        }
      }
    });

    // We also automatically assign a recall event for the banner to an element with the right ID.
    let recallButton = document.getElementById("biskoui-button-show-banner");
    if (recallButton) {
      recallButton.onclick = function () {
        //@ts-ignore
        window.biskouiShowBanner();
      };
    }
    Array.from(
      document.getElementsByClassName("biskoui-button-show-banner")
    ).forEach((element) => {
      //@ts-ignore
      element.onclick = function () {
        //@ts-ignore
        window.biskouiShowBanner();
      };
    });
  }

  if (document.readyState !== "loading") {
    initialize();
  } else {
    // Important to set the once option, as some tags might re-trigger the DOMContentLoaded event which
    // can lead into an infinite biskoui loading loop (with biskoui_ready triggered, then another script triggered,
    // then biskoui_ready re-triggered, etc (happens with consent mode configurations).
    document.addEventListener("DOMContentLoaded", initialize, { once: true });
  }
})();
